import type { BillingStatus, UsageItem } from "../api/types";
import { isAtLimit } from "./check-limit";

export type EligibilityAction = "offer-creation" | "active-search";
export type EligibilityMode = "allow" | "warn" | "block";
export type EligibilityReason =
  | "billing_degraded"
  | "quota_exhausted"
  | "grace"
  | "none";

export type EligibilityVerdict = {
  eligible: boolean;
  mode: EligibilityMode;
  reason: EligibilityReason;
  billingStatus?: BillingStatus;
  limitKey?: string;
  used?: number;
  limit?: number;
};

export type EligibilityInput = {
  subscriptionStatus: BillingStatus | undefined;
  usage: UsageItem[] | undefined;
  action: EligibilityAction;
};

/**
 * Billing statuses that hard-block entry (clearly lapsed / cannot act).
 * Source: SPEC A.3 + Decision D4.
 */
const BLOCK_STATUSES: ReadonlySet<BillingStatus> = new Set<BillingStatus>([
  "unpaid",
  "canceled",
  "past_due",
  "incomplete_expired",
  "paused",
]);

/**
 * Billing statuses that let the user through with a non-blocking warning
 * (access may still be valid). Source: Decision D4.
 */
const WARN_STATUSES: ReadonlySet<BillingStatus> = new Set<BillingStatus>([
  "grace_period",
  "incomplete",
]);

/** The only quota pre-checked at entry (offer-creation). Active-search is billing-only (D6). */
const OFFER_LIMIT_KEY = "max_active_job_posts";

/**
 * Pure eligibility verdict for a guarded entry point.
 *
 * Precedence: hard-degraded billing → quota exhausted (offer-creation only) →
 * soft-degraded warning → allow. Absent/undefined inputs fail **open** (eligible)
 * so a paying customer is never falsely blocked on slow/failed billing data (D7).
 */
export const getEligibility = ({
  subscriptionStatus,
  usage,
  action,
}: EligibilityInput): EligibilityVerdict => {
  // 1. Hard-degraded billing → block (highest precedence)
  if (subscriptionStatus && BLOCK_STATUSES.has(subscriptionStatus)) {
    return {
      eligible: false,
      mode: "block",
      reason: "billing_degraded",
      billingStatus: subscriptionStatus,
    };
  }

  // 2. Quota exhausted for offer-creation → block (active-search skips this, D6)
  if (action === "offer-creation" && usage) {
    const item = usage.find((u) => u.key === OFFER_LIMIT_KEY);
    if (item && isAtLimit(item.used, item.limit)) {
      return {
        eligible: false,
        mode: "block",
        reason: "quota_exhausted",
        limitKey: item.key,
        used: item.used,
        limit: item.limit,
        billingStatus: subscriptionStatus,
      };
    }
  }

  // 3. Soft-degraded billing (grace/incomplete) → warn, but let through
  if (subscriptionStatus && WARN_STATUSES.has(subscriptionStatus)) {
    return {
      eligible: true,
      mode: "warn",
      reason: "grace",
      billingStatus: subscriptionStatus,
    };
  }

  // 4. Eligible — active/trialing/none-within-quota, or fail-open on absent data
  return {
    eligible: true,
    mode: "allow",
    reason: "none",
    billingStatus: subscriptionStatus,
  };
};
